// src/components/modals/AppModal.tsx
//
// The one modal chrome in the app (plan 2026-10-08-one-modal-component): a
// title bar on a hairline, a scrolling body, an optional footer on a second
// hairline, and the optional numbered stepper the pipeline wizard uses. It
// started life as `SourceSetupModal` (plan 2026-08-31) and was promoted once
// every other dialog was meant to look like it. Presentation only: no flow
// state, no data.
//
// A raw Mantine `<Modal>` outside this folder fails
// `__tests__/modalGate.test.ts`. If this component cannot express what you
// need, extend it here (or argue for a `MODAL_EXCEPTIONS` entry) — do not
// reach past it.
//
// FIVE THINGS HERE ARE LOAD-BEARING, not styling choices:
//
// 1. The layer. `zIndex` falls back to the enclosing layer
//    (`useModalLayerZIndex`), and whatever this modal ends up at is published
//    again via ModalLayerProvider. So a modal opened from inside a raised layer
//    (the wizard runs at 300 to clear the assistant drawer) matches that layer
//    without its caller knowing — ADR-0124 by construction. Forgetting either
//    half buries the nested modal under its own parent's overlay, which
//    shipped once; see components/modals/ModalLayer.tsx. Outside any layer
//    both values are `undefined`, which is exactly Mantine's default (200).
//
// 2. The Modal's own content is `overflow: hidden` so the header, the pinned
//    region and the footer stay put, which means the BODY carries the scroll.
//    Mantine's Modal content scrolls by itself (styles.css: `overflow-y: auto`
//    with a 90dvh cap) — take that away without giving the body its own bound
//    and there is no scrollbar at all, just content below the fold.
//
//    The bound is a FLEX CHAIN, not arithmetic: content and Mantine's body are
//    flex columns, the header/pinned/footer rows never shrink, and the scroll
//    box is `flex: 1; min-height: 0`. So the body simply takes whatever the
//    other rows leave inside Mantine's 90dvh cap. `bodyMaxHeight` still caps it
//    on a tall viewport, but nothing has to add up the chrome's height — which
//    is what would silently clip the footer the day a caller adds a row.
//
// 3. The body's min-height follows the size. 320px was tuned for the wizard;
//    on a two-field form it is 320px of empty body, and on a short viewport a
//    min-height beats the 90dvh cap and clips the footer out of reach. So
//    `sm`/`md` default to no min-height, `lg`/`xl` keep 320.
//
// 4. The title is rendered through `Modal.Title`, not a plain `Text`. Mantine
//    sets `aria-labelledby` only when ITS title is mounted
//    (ModalBaseContent: `ctx.titleMounted`), so a title in a `Text` leaves the
//    dialog without an accessible name — for a screen reader and for every
//    `getByRole("dialog", { name })`.
//
// 5. Borders use `--mantine-color-default-border`, not
//    `var(--app-border, …gray-2)`. `--app-border` is defined nowhere in the
//    app, so that fallback is what always renders — a fixed light gray that
//    does not survive the dark scheme.

import type { ReactNode, Ref } from "react";
import {
  Box,
  Group,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  type ModalProps,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

import { ModalLayerProvider, useModalLayerZIndex } from "./ModalLayer";

const BORDER = "1px solid var(--mantine-color-default-border)";

/** The only widths a modal comes in. `xxl` is the two-pane explorer width
 *  (the dashboard's data source structure view, 2026-10-14): 1160px, capped
 *  at the viewport so it never runs off a laptop screen. */
export const APP_MODAL_SIZES = {
  sm: 440,
  md: 640,
  lg: 840,
  xl: 1040,
  xxl: "min(1160px, 92vw)",
} as const;

export type AppModalSize = keyof typeof APP_MODAL_SIZES;

/** See note 3 — the default body min-height per size. */
const DEFAULT_BODY_MIN_HEIGHT: Record<AppModalSize, number> = {
  sm: 0,
  md: 0,
  lg: 320,
  xl: 320,
  xxl: 320,
};

export type AppModalStep = {
  key: string;
  label: string;
};

export type AppModalProps = {
  opened: boolean;
  onClose: () => void;
  /** Shown top-left, bold. A name, not a sentence. Also the dialog's accessible name. */
  title: ReactNode;
  /** One line of orientation under the title. */
  subtitle?: ReactNode;
  /** Mark left of the title — a ThemeIcon or a ConnectorIcon. */
  leading?: ReactNode;
  /** Omit for a single-step surface: no steps, no stepper row. */
  steps?: AppModalStep[];
  /** Index into `steps` of the step being shown. */
  activeStep?: number;
  /** Paints every step green — for a success screen past the last step. */
  allStepsComplete?: boolean;
  /**
   * Pinned between the header and the scrolling body: content that must stay
   * in view no matter how far the body scrolls. The BigQuery review step puts
   * the package-name field here — it is the one input on a step that is
   * otherwise a long list, and a required field that scrolls out of sight
   * reads as absent. Omit for no pinned region at all, hairline included.
   */
  stickyTop?: ReactNode;
  /** Omit for no footer at all, hairline included. */
  footer?: ReactNode;
  /**
   * `"muted"` tints the footer band (a secondary note under the main content,
   * e.g. the docs AI-reading modal) instead of the body background.
   */
  footerTone?: "default" | "muted";
  /**
   * Raised elevation for a surface that must clear another overlay — i.e. the
   * OWNER of a layer. Omit everywhere else: the modal then matches the layer
   * it was opened from (note 1), or Mantine's default outside any layer.
   */
  zIndex?: number;
  size?: AppModalSize;
  /**
   * `"top"` for a modal whose height changes while it is open (a multi-step
   * flow): centred, it would jump vertically on every step.
   */
  align?: "center" | "top";
  /** Shows the title bar's ×. `false` for a blocking step that must be answered. */
  withCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  /** For a modal whose content must stay readable behind it, e.g. a live preview. */
  overlayProps?: ModalProps["overlayProps"];
  /** `"none"` for content that runs edge to edge (a table, an editor). */
  bodyPadding?: "default" | "none";
  /** See note 2 — the single height knob. */
  bodyMaxHeight?: string;
  /** Defaults per size — see note 3. */
  bodyMinHeight?: number | string;
  bodyRef?: Ref<HTMLDivElement>;
  "data-testid"?: string;
  children: ReactNode;
};

export function AppModal({
  opened,
  onClose,
  title,
  subtitle,
  leading,
  steps,
  activeStep = 0,
  allStepsComplete = false,
  stickyTop,
  footer,
  footerTone = "default",
  zIndex,
  size = "lg",
  align = "center",
  withCloseButton = true,
  closeOnClickOutside,
  closeOnEscape,
  overlayProps,
  bodyPadding = "default",
  bodyMaxHeight = "calc(80vh - 180px)",
  bodyMinHeight,
  bodyRef,
  "data-testid": dataTestId,
  children,
}: AppModalProps) {
  const enclosingLayer = useModalLayerZIndex();
  const layer = zIndex ?? enclosingLayer;
  const padded = bodyPadding === "default";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered={align === "center"}
      size={APP_MODAL_SIZES[size]}
      radius="lg"
      padding={0}
      withCloseButton={false}
      zIndex={layer}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      overlayProps={overlayProps}
      data-testid={dataTestId}
      styles={{
        content: { overflow: "hidden", display: "flex", flexDirection: "column" },
        body: { padding: 0, display: "flex", flexDirection: "column", minHeight: 0 },
      }}
    >
      <ModalLayerProvider zIndex={layer}>
        <Box
          px="xl"
          pt="lg"
          pb="md"
          style={{ borderBottom: BORDER, flexShrink: 0 }}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" align="center" wrap="nowrap">
              {leading}
              <Stack gap={2}>
                <Modal.Title
                  style={{
                    fontWeight: 700,
                    fontSize: "var(--mantine-font-size-lg)",
                    lineHeight: "var(--mantine-line-height-lg)",
                  }}
                >
                  {title}
                </Modal.Title>
                {subtitle && (
                  <Text size="sm" c="dimmed">
                    {subtitle}
                  </Text>
                )}
              </Stack>
            </Group>
            {withCloseButton && (
              <UnstyledButton onClick={onClose} aria-label="Close">
                <Text size="xl" c="dimmed" lh={1}>
                  &times;
                </Text>
              </UnstyledButton>
            )}
          </Group>

          {steps && steps.length > 0 && (
            <Group gap={0} wrap="nowrap" mt="md">
              {steps.map((s, i) => {
                const isCompleted = allStepsComplete || i < activeStep;
                const isActive = !allStepsComplete && i === activeStep;
                const isLast = i === steps.length - 1;

                return (
                  <Group key={s.key} gap={0} wrap="nowrap" style={{ flex: 1 }}>
                    <Group gap="xs" wrap="nowrap">
                      <ThemeIcon
                        size={28}
                        radius="xl"
                        variant={isCompleted || isActive ? "filled" : "light"}
                        color={isCompleted ? "green" : isActive ? "blue" : "gray"}
                      >
                        {isCompleted ? (
                          <IconCheck size={14} />
                        ) : (
                          <Text size="xs" fw={600}>
                            {i + 1}
                          </Text>
                        )}
                      </ThemeIcon>
                      <Text
                        size="sm"
                        fw={isActive ? 600 : 400}
                        c={isActive ? undefined : isCompleted ? "green" : "dimmed"}
                      >
                        {s.label}
                      </Text>
                    </Group>

                    {!isLast && (
                      <Box
                        mx="sm"
                        style={{
                          flex: 1,
                          height: 1,
                          backgroundColor: isCompleted
                            ? "var(--mantine-color-green-4)"
                            : "var(--mantine-color-default-border)",
                        }}
                      />
                    )}
                  </Group>
                );
              })}
            </Group>
          )}
        </Box>

        {stickyTop && (
          <Box px="xl" py="md" style={{ borderBottom: BORDER, flexShrink: 0 }}>
            {stickyTop}
          </Box>
        )}

        <Box
          ref={bodyRef}
          px={padded ? "xl" : 0}
          py={padded ? "lg" : 0}
          style={{
            flex: 1,
            minHeight: bodyMinHeight ?? DEFAULT_BODY_MIN_HEIGHT[size],
            maxHeight: bodyMaxHeight,
            overflow: "auto",
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            px="xl"
            py="md"
            style={{
              borderTop: BORDER,
              flexShrink: 0,
              background:
                footerTone === "muted"
                  ? "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))"
                  : undefined,
            }}
          >
            {footer}
          </Box>
        )}
      </ModalLayerProvider>
    </Modal>
  );
}
